namespace Solovis.Reports.Parameters.AttributionReport {

  export interface AttributionTimePeriodOptions {
    // One of the period codes below, or "sinceDates".
    selectedPeriod?: string;
    // Start dates for the "Since Dates" option (end date is the As of Date parameter).
    sinceDayIds?: number[];
  }

  interface PeriodChoice { code: string; text: string; }

  // Period options shown in the Edit Time Periods modal (AR-010), in mockup order.
  const PERIOD_ROW_1: PeriodChoice[] = [
    { code: "mtd", text: "MTD" },
    { code: "qtd", text: "QTD" },
    { code: "ytd", text: "YTD" },
    { code: "fytd", text: "FYTD" }
  ];
  const PERIOD_ROW_2: PeriodChoice[] = [
    { code: "oneYear", text: "1Y" },
    { code: "threeYears", text: "3Y" },
    { code: "fiveYears", text: "5Y" },
    { code: "sevenYears", text: "7Y" },
    { code: "tenYears", text: "10Y" }
  ];
  const SINCE_INCEPTION: PeriodChoice = { code: "sinceInception", text: "Since Inception" };
  const SINCE_DATES_CODE = "sinceDates";

  const SinceDatesHelp = "Create intervals that range from each specified date until the As of Date.";

  // A "Since Dates" start date must fall within the supported date range and cannot be in the future.
  const SINCE_DATE_MIN = InvData.Util.DateIndexHelper.IndexZeroDate;
  const SINCE_DATE_INVALID_MESSAGE = "Invalid since date. Please enter a date within the available data range.";

  // Widens the "Since Dates" picker's own min/max so it never silently swallows an out-of-range
  // typed date before we can see it (the kendo widget rejects typed dates outside its configured
  // min/max instead of surfacing them) — the actual floor/future check happens in "validate" below.
  // Note: Date years 0-99 get shifted to 1900-1999 by the Date constructor, so 1000 (not 1) avoids that trap.
  const WIDE_OPEN_MIN = new Date(1000, 0, 1);
  const WIDE_OPEN_MAX = new Date(9999, 0, 1);

  /**
   * A "Since Dates" start date is valid when it falls on or after SINCE_DATE_MIN and is not in
   * the future. A null/undefined date (nothing entered yet) is treated as valid -- there's
   * nothing to flag. `now` is injectable so this stays deterministic in tests.
   */
  export function isSinceDateValid(date: Date | null, now: Date = new Date()): boolean {
    if (!date) { return true; }
    return date >= SINCE_DATE_MIN && date <= now;
  }

  /**
   * "Time Period" parameters-bar control for the Attribution Report. Renders a Configure link
   * that opens the Edit Time Periods modal (AR-010). Exactly one period can be selected at a time.
   */
  export class AttributionTimePeriodsParameterControl extends Solovis.UI.BaseControl {
    private _options: AttributionTimePeriodOptions;

    constructor(options) {
      super(options);
      this._options = (typeof options.initialValue !== "undefined" && options.initialValue !== null)
        ? options.initialValue
        : {};
    }

    public doRender() {
      const self = this;

      const configureLink = $("<a>").text("Configure").appendTo(this.container);
      configureLink.click(function () { self._showEditor(); });

      const editLink = $("<a>").appendTo(this.container)
        .css("padding-left", "5px")
        .html("<span class='fa fa-pencil'></span>");
      editLink.click(function () { self._showEditor(); });
    }

    private _showEditor() {
      const editorDialog = new EditTimePeriodsDialog({
        options: this._options,
        saveCallback: (data: AttributionTimePeriodOptions) => this.setParameterValue(data)
      });
      editorDialog.show();
    }

    public _setCurrentOptions(data) {
      this._options = data ?? {};
    }

    public setParameterValue(newValue, more?) {
      Solovis.Reports.Parameters.ParameterControlHelper.handleSetParameterValue(
        this, AttributionTimePeriodsParameterControl, this._setCurrentOptions, newValue, more);
    }

    public getParameterValue() { return Solovis.deepCopy(this._options); }

    public notifyParameterValueChanged() { $(this).trigger("parameterValueChanged"); }
  }


  export class EditTimePeriodsDialog {
    private _opts: AttributionTimePeriodOptions;
    private _saveCallback: (data: AttributionTimePeriodOptions) => void;
    private _formBuilder: Solovis.UI.InputControls.FormBuilder;

    public constructor(options: { options: AttributionTimePeriodOptions; saveCallback: (data: AttributionTimePeriodOptions) => void; }) {
      this._opts = options.options ? Solovis.deepCopy(options.options) : {};
      this._saveCallback = options.saveCallback;
    }

    public show() {
      const opts = this._opts;

      const formBuilder = new Solovis.UI.InputControls.FormBuilder({
        container: $("<div>"),
        width: 600,
        labelColumnWidth: 120
      });
      this._formBuilder = formBuilder;

      formBuilder.addEntry({
        name: "timePeriod",
        label: "",
        showValueOnFullRow: true,
        hideLabelElement: true,

        createEditor: (createEditorOptions) => {
          const container = createEditorOptions.container;
          container.css({ "padding": "8px 0 8px 80px", "display": "flex", "flex-direction": "column", "gap": "18px", "align-items": "flex-start" });

          // Fixed-width cells so each column lines up vertically across rows (MTD above 1Y, etc.).
          const CELL_WIDTH = "72px";

          const checkBoxes: { code: string; instance: any; }[] = [];
          let sinceDatesListBuilder: Solovis.UI.VerticalOrderedListBuilder = null;

          // Enforce single-selection across the named periods: checking one clears the others.
          const enforceSingleSelection = (selectedCode: string) => {
            checkBoxes.forEach((cb) => {
              if (cb.code !== selectedCode) { cb.instance.option("value", false); }
            });
          };

          const makeCheckBox = (row: JQuery, choice: PeriodChoice, initialValue: boolean, fixedWidth: boolean = true) => {
            const cell = $("<div>").appendTo(row);
            if (fixedWidth) { cell.css({ "width": CELL_WIDTH }); }
            const instance = $("<div>").appendTo(cell).dxCheckBox({
              text: choice.text,
              value: initialValue,
              onValueChanged: (e) => { if (e.value) { enforceSingleSelection(choice.code); } }
            }).dxCheckBox("instance");
            checkBoxes.push({ code: choice.code, instance: instance });
          };

          const createFlexRow = () => $("<div>").css({ "display": "flex", "flex-direction": "row" }).appendTo(container);

          const row1 = createFlexRow();
          PERIOD_ROW_1.forEach((c) => makeCheckBox(row1, c, opts.selectedPeriod === c.code));

          const row2 = createFlexRow();
          PERIOD_ROW_2.forEach((c) => makeCheckBox(row2, c, opts.selectedPeriod === c.code));

          const row3 = createFlexRow();
          makeCheckBox(row3, SINCE_INCEPTION, opts.selectedPeriod === SINCE_INCEPTION.code, false);

          // Since Dates: label + help icon, with the Add Dates picker inline on the same row (no checkbox).
          // Adding one or more dates selects the Since Dates period; the end date is the As of Date parameter.
          const sinceRow = $("<div>").css({ "display": "flex", "flex-direction": "row", "align-items": "flex-start", "gap": "8px" }).appendTo(container);
          $("<span>").text("Since Dates").appendTo(sinceRow);
          const helpLink = $("<a>").addClass("fa fa-question-circle").css("cursor", "pointer").appendTo(sinceRow);
          helpLink.click(() => {
            Solovis.Site.HelpManager.showCustomHelpDialog(SinceDatesHelp, {
              title: "Since Dates",
              portalServices: createEditorOptions.formBuilder ? createEditorOptions.formBuilder.portalServices() : null
            });
          });

          const sinceDatesContainer = $("<div>").appendTo(sinceRow);
          const initialDates = (opts.sinceDayIds && opts.sinceDayIds.length)
            ? _.map(opts.sinceDayIds, (dayId: number) => InvData.DayId.toDate(dayId))
            : null;

          sinceDatesListBuilder = new Solovis.UI.VerticalOrderedListBuilder({
            controlHost: sinceDatesContainer,
            itemTypeLabel: "Date",
            showEditLinks: false,
            values: initialDates,
            editorCreator: function (editorDiv, initialValue) {
              const inputContainer = $("<div>").appendTo(editorDiv);

              const inputEditor = new Solovis.UI.InputControl({
                container: inputContainer,
                type: "date",
                value: initialValue,
                min: WIDE_OPEN_MIN,
                max: WIDE_OPEN_MAX,
                // No validationModel passed -- this makes InputControl render its own inline error
                // right on this row, instead of sharing one model across rows (which only reflects
                // the most recently validated row and can clear a still-invalid row's message).
                validate: function () {
                  const date = this.value();
                  if (!date || isSinceDateValid(date)) {
                    return { isValid: true, message: null };
                  }
                  return { isValid: false, message: SINCE_DATE_INVALID_MESSAGE };
                }
              });

              return {
                getValue: function () {
                  const date = inputEditor.value();
                  return date ? InvData.DayId.forDate(date) : null;
                },
                isValid: () => inputEditor.isValid()
              };
            },
            newValueGenerator: function () { return null; }
          });
          sinceDatesListBuilder.setAsCurrentControl();

          const editor = {
            getValue: (): AttributionTimePeriodOptions => {
              const result: AttributionTimePeriodOptions = {};
              const checked = _.find(checkBoxes, (cb) => cb.instance.option("value"));
              if (checked) {
                result.selectedPeriod = checked.code;
              } else {
                const dayIds = ((sinceDatesListBuilder.getValues() as number[]) ?? []).filter((d) => d != null);
                if (dayIds.length) {
                  result.selectedPeriod = SINCE_DATES_CODE;
                  result.sinceDayIds = dayIds;
                }
              }
              return result;
            },
            isValid: (): boolean => sinceDatesListBuilder.getEditors()
              .every((editorInfo) => editorInfo.isValid())
          };

          return editor;
        }
      });

      formBuilder.setupDone();

      formBuilder.showAsDialog({
        title: "Edit Time Periods",
        desiredWidth: 640,
        desiredHeight: 360,
        showSaveCancel: true,
        saveButtonText: "OK",
        saveCallback: () => {
          if (this._formBuilder.hasErrors()) {
            return false;
          }
          const newOptions = this._formBuilder.getValueByName("timePeriod") as AttributionTimePeriodOptions;
          this._saveCallback(newOptions ?? {});
          return true;
        }
      });
    }
  }
}
